import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';

const MINI_STATES = [
  { key: 'visible', label: 'Visible', active: 'bg-green-600 text-white' },
  { key: 'oos', label: 'Agotado', active: 'bg-red-500 text-white' },
  { key: 'hidden', label: 'Oculto', active: 'bg-slate-700 text-white' },
];

export const MiniStateButtons = ({ state, onSet, testPrefix }) => (
  <div className="inline-flex rounded overflow-hidden border border-slate-200 shrink-0">
    {MINI_STATES.map(s => (
      <button key={s.key} type="button" onClick={() => onSet(s.key)}
        className={`px-2 py-0.5 text-[10px] font-semibold border-r last:border-r-0 border-slate-200 transition-colors ${state === s.key ? s.active : 'bg-white text-slate-500 hover:bg-slate-50'}`}
        data-testid={`${testPrefix}-${s.key}`}>
        {s.label}
      </button>
    ))}
  </div>
);

export const AddOptionInput = ({ onAdd }) => {
  const [val, setVal] = useState('');
  const add = () => { const v = val.trim(); if (v) { onAdd(v); setVal(''); } };
  return (
    <div className="flex gap-1.5">
      <input type="text" value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder="Nueva opción…"
        className="flex-1 px-2 py-0.5 border border-slate-200 rounded text-[11px] focus:outline-none focus:ring-1 focus:ring-cyan-500" />
      <button type="button" onClick={add} className="px-2 py-0.5 bg-slate-200 text-slate-700 text-[10px] font-bold rounded hover:bg-slate-300">Añadir</button>
    </div>
  );
};

export const SizeChipsEditor = ({ sizes, sizeInput, setSizeInput, addSize, removeSize, moveSize, sizeDragRef }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1">Tallas</label>
    <div className="flex flex-wrap gap-1.5 mb-2">
      {sizes.map((s, si) => (
        <span key={s}
          draggable
          onDragStart={() => { sizeDragRef.current = si; }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (sizeDragRef.current !== null) moveSize(sizeDragRef.current, si);
            sizeDragRef.current = null;
          }}
          className="inline-flex items-center gap-1 px-1.5 py-1 bg-slate-100 border border-slate-300 rounded text-xs font-medium text-slate-700 cursor-grab active:cursor-grabbing"
          data-testid={`edit-size-chip-${s}`}>
          <button type="button" onClick={() => moveSize(si, si - 1)} disabled={si === 0}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-25 text-[10px] leading-none font-bold"
            title="Mover a la izquierda" data-testid={`edit-size-left-${s}`}>◀</button>
          {s}
          <button type="button" onClick={() => moveSize(si, si + 1)} disabled={si === sizes.length - 1}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-25 text-[10px] leading-none font-bold"
            title="Mover a la derecha" data-testid={`edit-size-right-${s}`}>▶</button>
          <button type="button" onClick={() => removeSize(s)} className="text-red-400 hover:text-red-600 font-bold leading-none" data-testid={`edit-remove-size-${s}`}>×</button>
        </span>
      ))}
    </div>
    <div className="flex gap-2">
      <input type="text" value={sizeInput} onChange={(e) => setSizeInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSize(); } }}
        placeholder='Ej: "Adulto M" o "Niño 5-6"'
        className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
        data-testid="edit-size-input" />
      <button type="button" onClick={addSize} className="px-3 py-1.5 bg-slate-700 text-white text-xs font-semibold rounded-md hover:bg-slate-800" data-testid="edit-add-size">Añadir</button>
    </div>
    <p className="text-[10px] text-slate-400 mt-1">Usa el prefijo &quot;Adulto&quot; o &quot;Niño&quot; para que se traduzca automáticamente al inglés. Arrastra las tallas o usa ◀ ▶ para cambiar su orden — ese orden se muestra en la tienda, Inventario y Stock.</p>
  </div>
);

export const ColorPhotosEditor = ({
  colors, setColors, addColor, removeColor,
  removeExistingImg, moveExisting, removeNewFile, addFiles, openPicker, dragRef,
}) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-xs font-semibold text-slate-600">Colores + Fotos</label>
      <button type="button" onClick={addColor} className="text-xs text-cyan-600 font-semibold" data-testid="edit-add-color-btn">+ Añadir color</button>
    </div>
    <p className="text-[10px] text-slate-500 mb-2 leading-tight">
      💡 La <strong>primera foto</strong> de cada color es la portada. Arrastra las fotos o usa ◀ ▶ para reordenarlas — la que quede primero será la nueva portada.
    </p>
    <div className="space-y-3">
      {colors.map((c, ci) => (
        <div key={c.uid || ci} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50">
          <div className="flex items-center gap-2 mb-2">
            <input type="color" value={c.hex}
              onChange={(e) => setColors(prev => prev.map((x, i) => i === ci ? { ...x, hex: e.target.value, userSetHex: true } : x))}
              className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
              title="Se detecta solo al subir la primera foto — puedes ajustarlo"
              data-testid={`edit-color-hex-${ci}`} />
            <input type="text" value={c.name}
              onChange={(e) => setColors(prev => prev.map((x, i) => i === ci ? { ...x, name: e.target.value } : x))}
              placeholder="Nombre del color"
              className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid={`edit-color-name-${ci}`} />
            {colors.length > 1 && (
              <button type="button" onClick={() => removeColor(ci)} className="text-red-500 hover:text-red-700" data-testid={`edit-remove-color-${ci}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          {(c.existing.length > 0 || c.files.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {c.existing.map((url, ii) => (
                <div key={url} className="flex flex-col items-center gap-0.5">
                  <div
                    className="relative cursor-grab active:cursor-grabbing"
                    draggable
                    onDragStart={() => { dragRef.current = { ci, ii }; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const d = dragRef.current;
                      if (d && d.ci === ci) moveExisting(ci, d.ii, ii);
                      dragRef.current = null;
                    }}
                    data-testid={`edit-existing-img-${ci}-${ii}`}
                  >
                    <img src={url} alt="" className="w-14 h-14 object-cover rounded border border-slate-300 pointer-events-none" />
                    {ii === 0 && <span className="absolute bottom-0 left-0 right-0 bg-cyan-600/90 text-white text-[8px] text-center rounded-b font-bold">PORTADA</span>}
                    <button type="button" onClick={() => removeExistingImg(ci, ii)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] font-bold leading-none flex items-center justify-center"
                      data-testid={`edit-remove-existing-${ci}-${ii}`}>×</button>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button type="button" onClick={() => moveExisting(ci, ii, ii - 1)}
                      disabled={ii === 0}
                      className="w-5 h-4 text-[10px] leading-none rounded bg-slate-200 text-slate-600 hover:bg-slate-300 disabled:opacity-30"
                      title="Mover a la izquierda"
                      data-testid={`edit-move-left-${ci}-${ii}`}>◀</button>
                    <button type="button" onClick={() => moveExisting(ci, ii, ii + 1)}
                      disabled={ii === c.existing.length - 1}
                      className="w-5 h-4 text-[10px] leading-none rounded bg-slate-200 text-slate-600 hover:bg-slate-300 disabled:opacity-30"
                      title="Mover a la derecha"
                      data-testid={`edit-move-right-${ci}-${ii}`}>▶</button>
                  </div>
                </div>
              ))}
              {c.files.map((f, fi) => (
                <div key={`${f.name}-${f.size}-${f.lastModified}`} className="relative">
                  <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 object-cover rounded border-2 border-emerald-400" />
                  <span className="absolute bottom-0 left-0 right-0 bg-emerald-500/90 text-white text-[8px] text-center rounded-b font-bold">NUEVA</span>
                  <button type="button" onClick={() => removeNewFile(ci, fi)}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] font-bold leading-none flex items-center justify-center">×</button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <label className="flex-1 flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:bg-white text-xs text-slate-600">
              <span>📸 Añadir fotos nuevas</span>
              <input type="file" multiple accept="image/*" className="hidden"
                onChange={(e) => addFiles(ci, e.target.files)}
                data-testid={`edit-color-files-${ci}`} />
            </label>
            <button type="button" onClick={() => openPicker(ci)}
              className="px-3 py-2 border-2 border-dashed border-cyan-300 rounded text-xs text-cyan-700 hover:bg-cyan-50 font-semibold"
              data-testid={`edit-open-library-${ci}`}>
              📂 Fotos subidas
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PhotoLibraryModal = ({ libFiles, colors, pickerFor, onPick, onDelete, onClose }) => (
  <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-library-modal">
    <div className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h4 className="font-bold text-slate-800 text-sm">📂 Fotos subidas anteriormente</h4>
          <p className="text-[10px] text-slate-400">Toca una foto para añadirla al color · 🗑 elimina fotos sin uso. {libFiles ? `${libFiles.length} fotos` : ''}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg" data-testid="close-photo-library">✕</button>
      </div>
      <div className="p-4 overflow-y-auto">
        {libFiles === null && (
          <p className="text-center text-slate-400 text-sm py-8">Cargando fotos…</p>
        )}
        {libFiles !== null && libFiles.length === 0 && (
          <p className="text-center text-slate-400 text-sm py-8">No hay fotos subidas todavía.</p>
        )}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {(libFiles || []).map(f => {
            const included = colors[pickerFor]?.existing.includes(f.url);
            return (
              <div key={f.file_id} className="relative">
                <button type="button" onClick={() => onPick(f.url)}
                  className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${included ? 'border-emerald-500 ring-2 ring-emerald-300' : 'border-slate-200 hover:border-cyan-400'}`}
                  title={f.original_filename || ''}
                  data-testid={`library-photo-${f.file_id}`}>
                  <img src={f.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  {included && <span className="absolute top-1 right-1 w-5 h-5 bg-emerald-500 text-white rounded-full text-xs font-bold flex items-center justify-center">✓</span>}
                  {f.in_use && <span className="absolute bottom-0 left-0 right-0 bg-cyan-600/90 text-white text-[8px] text-center font-bold">EN USO</span>}
                </button>
                {!f.in_use && (
                  <button type="button" onClick={() => onDelete(f)}
                    className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] font-bold leading-none flex items-center justify-center shadow hover:bg-red-600"
                    title="Eliminar de la biblioteca"
                    data-testid={`library-delete-${f.file_id}`}>🗑</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="px-4 py-2.5 border-t border-slate-200 flex justify-end">
        <button onClick={onClose}
          className="px-4 py-1.5 bg-cyan-600 text-white text-xs font-semibold rounded-lg hover:bg-cyan-700"
          data-testid="done-photo-library">Listo</button>
      </div>
    </div>
  </div>
);

export const BadgesEditor = ({ badges, setBadges }) => {
  const [val, setVal] = useState('');
  const add = () => {
    const v = val.trim();
    if (v && !badges.includes(v)) setBadges([...badges, v]);
    setVal('');
  };
  return (
    <div data-testid="badges-editor">
      <label className="block text-xs font-semibold text-slate-600 mb-1">
        Badges del producto
        <span className="ml-2 font-normal text-slate-400">etiquetas visibles en catálogo y producto</span>
      </label>
      <div className="space-y-1.5">
        {badges.map((b, i) => (
          <div key={`badge-${i}`} className="flex items-center gap-1.5">
            <input type="text" value={b}
              onChange={(e) => setBadges(badges.map((x, j) => j === i ? e.target.value : x))}
              className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid={`badge-input-${i}`} />
            <span className="inline-flex items-center bg-slate-900 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">{b || '—'}</span>
            <button type="button" onClick={() => setBadges(badges.filter((_, j) => j !== i))}
              className="text-red-500 hover:text-red-700" title="Eliminar badge" data-testid={`badge-delete-${i}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-1.5">
        <input type="text" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder='Ej: "Edición Limitada"'
          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
          data-testid="badge-new-input" />
        <button type="button" onClick={add}
          className="px-3 py-1 bg-slate-700 text-white text-xs font-semibold rounded hover:bg-slate-800"
          data-testid="badge-add-btn">Añadir badge</button>
      </div>
    </div>
  );
};

export const SelectionsEditor = ({
  selGroups, setGroupLabel, setGroupAll, setGroupOptState, setGroupOptLabel,
  removedFields = [], onDeleteGroup, onRestoreGroup,
  customSels, setCustomSels, newSelName, setNewSelName, newSelOpts, setNewSelOpts, addCustomSel,
}) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-3" data-testid="edit-selections-section">
    <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
      Selecciones y opciones
      <span className="ml-2 normal-case font-normal text-slate-400">nombre + estado de cada una</span>
    </p>
    {selGroups.map((g, gi) => {
      if (removedFields.includes(g.field)) return null;
      const allSame = g.options.every(o => o.state === g.options[0]?.state) ? g.options[0]?.state : 'mixed';
      return (
        <div key={g.field} className="bg-white border border-slate-200 rounded-md p-2 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="text" value={g.label} onChange={(e) => setGroupLabel(gi, e.target.value)}
              className="flex-1 min-w-[120px] px-2 py-1 border border-slate-300 rounded text-xs font-bold focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid={`sel-group-name-${g.field}`} />
            <select value={allSame} onChange={(e) => setGroupAll(gi, e.target.value)}
              className="px-1.5 py-1 border border-slate-300 rounded text-[10px] font-semibold text-slate-600 focus:outline-none"
              title="Estado de toda la selección"
              data-testid={`sel-group-state-${g.field}`}>
              <option value="visible">Toda: Visible</option>
              <option value="oos">Toda: Agotada</option>
              <option value="hidden">Toda: Oculta</option>
              {allSame === 'mixed' && <option value="mixed" disabled>Mixta</option>}
            </select>
            <button type="button" onClick={() => onDeleteGroup(gi)}
              className="text-red-500 hover:text-red-700"
              title="Eliminar esta selección por completo"
              data-testid={`sel-group-delete-${g.field}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {g.options.map((o, oi) => (
            <div key={o.key} className="flex items-center justify-between gap-2">
              {g.editableLabels ? (
                <input type="text" value={o.label}
                  onChange={(e) => setGroupOptLabel(gi, oi, e.target.value)}
                  placeholder={o.defaultLabel}
                  className="flex-1 min-w-0 px-2 py-0.5 border border-slate-200 rounded text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid={`sel-opt-label-${g.field}-${o.key}`} />
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                  {o.dot && <span className="w-3 h-3 rounded-full ring-1 ring-slate-300" style={{ backgroundColor: o.dot }} />}
                  {o.label}
                </span>
              )}
              <MiniStateButtons state={o.state} onSet={(s) => setGroupOptState(gi, oi, s)} testPrefix={`sel-${g.field}-${o.key.replace(/[^a-zA-Z0-9_]/g, '')}`} />
            </div>
          ))}
        </div>
      );
    })}

    {removedFields.length > 0 && (
      <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-400" data-testid="removed-selections-row">
        <span className="font-semibold uppercase">Eliminadas:</span>
        {selGroups.filter(g => removedFields.includes(g.field)).map(g => (
          <button key={g.field} type="button" onClick={() => onRestoreGroup(g.field)}
            className="underline hover:text-cyan-600"
            title="Restaurar esta selección"
            data-testid={`restore-sel-${g.field}`}>
            {g.label} · restaurar
          </button>
        ))}
      </div>
    )}

    {customSels.map((cs, ci) => (
      <div key={cs.id} className="bg-white border border-cyan-200 rounded-md p-2 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <input type="text" value={cs.name}
            onChange={(e) => setCustomSels(prev => prev.map((x, i) => i === ci ? { ...x, name: e.target.value } : x))}
            className="flex-1 min-w-[120px] px-2 py-1 border border-slate-300 rounded text-xs font-bold focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid={`custom-sel-name-${ci}`} />
          <MiniStateButtons state={cs.state || 'visible'}
            onSet={(s) => setCustomSels(prev => prev.map((x, i) => i === ci ? { ...x, state: s } : x))}
            testPrefix={`custom-sel-state-${ci}`} />
          <button type="button" onClick={() => setCustomSels(prev => prev.filter((_, i) => i !== ci))}
            className="text-red-500 hover:text-red-700" title="Eliminar selección" data-testid={`custom-sel-delete-${ci}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {(cs.options || []).map((o, oi) => (
          <div key={o.key} className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 flex-1 min-w-0">
              <input type="text" value={o.label}
                onChange={(e) => setCustomSels(prev => prev.map((x, i) => i === ci ? { ...x, options: x.options.map((y, j) => j === oi ? { ...y, label: e.target.value } : y) } : x))}
                className="flex-1 min-w-0 px-2 py-0.5 border border-slate-200 rounded text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid={`custom-opt-label-${ci}-${oi}`} />
              <button type="button"
                onClick={() => setCustomSels(prev => prev.map((x, i) => i === ci ? { ...x, options: x.options.filter((_, j) => j !== oi) } : x))}
                className="text-red-400 hover:text-red-600 font-bold text-xs px-1" title="Eliminar opción">×</button>
            </span>
            <MiniStateButtons state={o.state || 'visible'}
              onSet={(s) => setCustomSels(prev => prev.map((x, i) => i === ci ? { ...x, options: x.options.map((y, j) => j === oi ? { ...y, state: s } : y) } : x))}
              testPrefix={`custom-opt-${ci}-${oi}`} />
          </div>
        ))}
        <AddOptionInput onAdd={(label) => setCustomSels(prev => prev.map((x, i) => i === ci ? { ...x, options: [...x.options, { key: `opt_${Date.now()}`, label, state: 'visible' }] } : x))} />
      </div>
    ))}

    <div className="border-2 border-dashed border-slate-300 rounded-md p-2 space-y-1.5">
      <p className="text-[10px] font-bold text-slate-500 uppercase">➕ Crear selección nueva</p>
      <input type="text" value={newSelName} onChange={(e) => setNewSelName(e.target.value)}
        placeholder='Nombre (ej: "Tipo de nudo")'
        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
        data-testid="new-sel-name" />
      <input type="text" value={newSelOpts} onChange={(e) => setNewSelOpts(e.target.value)}
        placeholder='Opciones separadas por coma (ej: "Clásico, Doble, Reforzado")'
        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
        data-testid="new-sel-options" />
      <button type="button" onClick={addCustomSel}
        className="px-3 py-1 bg-slate-700 text-white text-xs font-semibold rounded hover:bg-slate-800"
        data-testid="add-custom-sel-btn">Añadir selección</button>
    </div>
  </div>
);
